/**
 * Las reglas de roles por base y tabla, en el navegador.
 *
 * Son las de extras_mg/roles/logica.py y tienen que seguir siendo las mismas:
 * si aqui se ofrece un boton que alla se niega, la persona se lleva un error al
 * hacer clic. pruebas/roles.test.mjs corre los mismos casos contra las dos.
 *
 * El objeto `permisos` es el que manda el backend (get_permissions_object del
 * manager 'mg_roles'); las listas de operaciones vienen de alla para no
 * duplicarlas.
 */

export const ENCARGADO = 'ENCARGADO'
export const EDITOR = 'EDITOR'
export const LECTOR = 'LECTOR'
export const SIN_ACCESO = 'SIN_ACCESO'
export const PASO = 'PASO'
export const ADMIN = 'ADMIN'
export const MIEMBRO = 'MIEMBRO'
// "Solo sus asuntos": lo pone el sistema a quien esta en Personas de una tabla
// que no ve (extras_mg/asignados). Nunca se da desde la pantalla.
export const ASIGNADO = 'ASIGNADO'
// El tipo de vista "Mis asuntos", la unica de esa tabla que ve.
export const TIPO_SOLO_ASIGNADOS = 'mg_solo_asignados'

// Los nombres que se leen (2026-10-07, Victor): el rol ENCARGADO se lee
// "Miembro de <nombre>" con el de la base o tabla ("Miembro de CANCÚN",
// "Miembro de Legal"), para que nadie tenga que saber que es una base; y el
// Miembro del espacio de trabajo, "Miembro del espacio". "Miembro de la base"
// queda para cuando no hay nombre. Los valores guardados no cambian.
export const miembroDe = (nombre) =>
  nombre ? `Miembro de ${nombre}` : NOMBRES[ENCARGADO]

export const NOMBRES = {
  [ADMIN]: 'Admin',
  [MIEMBRO]: 'Miembro del espacio',
  [ENCARGADO]: 'Miembro de la base',
  [EDITOR]: 'Editor',
  [LECTOR]: 'Lector',
  [SIN_ACCESO]: 'Sin acceso',
  [ASIGNADO]: 'Solo sus asuntos',
}

export const DESCRIPCIONES = {
  [ENCARGADO]: 'Edita y decide quién entra (como Editor o Lector).',
  [EDITOR]: 'Edita datos, columnas y vistas. No borra ni da permisos.',
  [LECTOR]: 'Solo mira. No cambia nada.',
  [SIN_ACCESO]: 'No lo ve.',
  [ASIGNADO]:
    'Solo las filas donde está en Personas. Lo da y lo quita el sistema.',
}

export function rolDeEspacio(permisos) {
  if (permisos === 'ADMIN') return ADMIN
  if (permisos === 'LECTOR') return LECTOR
  if (permisos === 'SIN_ACCESO') return SIN_ACCESO
  if (permisos === null || permisos === undefined) return null
  return MIEMBRO
}

export function esOperacionDeLectura(nombre, verbos, lecturasSinVerbo) {
  if ((lecturasSinVerbo || []).includes(nombre)) return true
  const verbo = String(nombre).split('.').pop()
  return (verbos || ['read', 'list', 'listen']).some(
    (v) => verbo === v || verbo.startsWith(v)
  )
}

/**
 * [base, tabla] de un contexto del frontend, [null, null] si es el espacio de
 * trabajo, y null si no se reconoce. Mira las formas que de verdad llegan.
 * `baseDeTabla(id)` busca la base de una tabla en el store.
 *
 * El campo va ANTES que la tabla: un campo también trae database_id (y su
 * propio id), así que con la tabla primero se leía como "la tabla <id del
 * campo>". Quien tenía su rol en la tabla y no en la base caía en su rol de
 * espacio ("Sin acceso") y veía todas las celdas en solo lectura (2026-10-29).
 */
export function ubicarContexto(context, workspaceId, baseDeTabla) {
  if (!context || typeof context !== 'object') {
    return null
  }
  // Vista, campo: traen table_id; la base se busca en el store.
  if (context.table_id !== undefined) {
    return [baseDeTabla(context.table_id), context.table_id]
  }
  // Una tabla: trae database_id.
  if (context.database_id !== undefined && context.id !== undefined) {
    return [context.database_id, context.id]
  }
  // Widget o fuente de datos de un tablero.
  if (context.dashboard_id !== undefined) {
    return [context.dashboard_id, null]
  }
  // Una aplicacion (base, tablero, app): tiene tipo y espacio.
  if (context.type !== undefined && context.workspace !== undefined) {
    return [context.id, null]
  }
  // El propio espacio de trabajo.
  if (context.id === workspaceId && context.users !== undefined) {
    return [null, null]
  }
  return null
}

/** Las bases donde la persona tiene alguna tabla que si ve. */
function basesConTablaVisible(permisos) {
  const bases = new Set()
  for (const [tabla, rol] of Object.entries(permisos.tablas || {})) {
    const base = (permisos.base_de_tabla || {})[tabla]
    if (rol !== SIN_ACCESO && base !== undefined) bases.add(Number(base))
  }
  return bases
}

/**
 * El rol que manda sobre esa cosa: tabla > base > espacio.
 * `null` si la persona no es del espacio (no se opina).
 */
export function rolEfectivo(permisos, aplicacionId, tablaId) {
  const base = rolDeEspacio(permisos?.rol_espacio)
  if (base === null || base === ADMIN) return base
  const tablas = permisos.tablas || {}
  const aplicaciones = permisos.aplicaciones || {}
  if (tablaId !== null && tablaId !== undefined && tablas[tablaId]) {
    const rolTabla = tablas[tablaId]
    if (rolTabla !== ASIGNADO) return rolTabla
    // ASIGNADO solo vale mientras no vea la tabla por otro lado (gemela de
    // Perfil.rol): si le abrieron la base o el espacio, manda eso.
    const debajo =
      aplicacionId !== null && aplicacionId !== undefined
        ? aplicaciones[aplicacionId] || base
        : base
    return debajo === SIN_ACCESO ? ASIGNADO : debajo
  }
  if (aplicacionId !== null && aplicacionId !== undefined) {
    const rol = aplicaciones[aplicacionId] || base
    if (
      (tablaId === null || tablaId === undefined) &&
      rol === SIN_ACCESO &&
      basesConTablaVisible(permisos).has(Number(aplicacionId))
    ) {
      return PASO
    }
    return rol
  }
  // El propio espacio: "Sin acceso" lo lee para llegar a lo que le compartieron.
  if (base === SIN_ACCESO) return PASO
  return base
}

/**
 * Si quien tiene el rol ASIGNADO en una tabla NO puede hacer esa operacion.
 * `esSuVista`: la operacion es sobre su vista "Mis asuntos". Gemela de
 * niega_asignado en logica.py; las listas vienen del backend.
 */
export function niegaAsignado(permisos, operacion, esSuVista) {
  if ((permisos.permitidas_siempre || []).includes(operacion)) return false
  if (String(operacion).startsWith('database.table.view.')) {
    return !(
      esSuVista && (permisos.permitidas_en_su_vista || []).includes(operacion)
    )
  }
  return !(permisos.permitidas_al_asignado || []).includes(operacion)
}

/**
 * Si el contexto es la vista "Mis asuntos" de esta persona en esa tabla. El
 * backend ademas comprueba que siga intacta; aqui basta con saber cual es.
 */
export function esSuVista(permisos, context, tablaId) {
  return (
    !!context &&
    context.ownership_type === TIPO_SOLO_ASIGNADOS &&
    Number((permisos.vistas_asignado || {})[tablaId]) === Number(context.id)
  )
}

export function niega(permisos, rol, operacion) {
  if ([null, undefined, ADMIN, MIEMBRO].includes(rol)) return false
  if (rol === ENCARGADO) {
    // Edita todo y decide quién entra, pero la base solo la borra un admin.
    return (permisos.negadas_al_encargado || []).includes(operacion)
  }
  if ((permisos.permitidas_siempre || []).includes(operacion)) return false
  if ((permisos.solo_de_quien_administra || []).includes(operacion)) return true
  const lectura = esOperacionDeLectura(
    operacion,
    permisos.verbos_de_lectura,
    permisos.lecturas_sin_verbo
  )
  if (rol === SIN_ACCESO) return true
  if (rol === LECTOR || rol === PASO) return !lectura
  if (rol === EDITOR) {
    return (permisos.negadas_al_editor || []).includes(operacion)
  }
  if (rol === ASIGNADO) {
    // Sin saber de que vista se trata, como si no fuera la suya.
    return niegaAsignado(permisos, operacion, false)
  }
  return !lectura
}

/**
 * Si dentro del espacio (aplicacionId null) o de esa base hay algo que la
 * persona no ve. Gemela de tiene_algo_oculto en logica.py.
 */
export function tieneAlgoOculto(permisos, aplicacionId) {
  if (rolDeEspacio(permisos?.rol_espacio) === ADMIN) return false
  const tablas = permisos.tablas || {}
  const deTabla = permisos.base_de_tabla || {}
  if (aplicacionId !== null && aplicacionId !== undefined) {
    const rol = rolEfectivo(permisos, aplicacionId, null)
    if (rol === SIN_ACCESO || rol === PASO) return true
    // Una tabla donde solo ve sus filas tambien es algo oculto.
    return Object.entries(tablas).some(
      ([t, r]) =>
        (r === SIN_ACCESO || r === ASIGNADO) &&
        Number(deTabla[t]) === Number(aplicacionId)
    )
  }
  if (rolDeEspacio(permisos?.rol_espacio) === SIN_ACCESO) return true
  return (
    Object.values(permisos.aplicaciones || {}).includes(SIN_ACCESO) ||
    Object.values(tablas).includes(SIN_ACCESO) ||
    Object.values(tablas).includes(ASIGNADO)
  )
}

/**
 * Exportar el espacio, duplicar una base, sacarle una instantanea o
 * restaurarla copian TODAS sus tablas: se le niegan a quien tiene algo oculto
 * dentro. Gemela de niega_copia en logica.py.
 */
export function niegaCopia(permisos, aplicacionId, operacion) {
  return (
    (permisos.operaciones_que_copian || []).includes(operacion) &&
    tieneAlgoOculto(permisos, aplicacionId)
  )
}

/** Admin del espacio, o Encargado de esa tabla o de su base. */
export function puedeAdministrar(permisos, aplicacionId, tablaId = null) {
  if (!permisos) return false
  if (permisos.rol_espacio === 'ADMIN') return true
  if (tablaId !== null && (permisos.tablas || {})[tablaId] === ENCARGADO) {
    return true
  }
  return (permisos.aplicaciones || {})[aplicacionId] === ENCARGADO
}

/** El objeto de permisos de un manager ('mg_roles'...) en ese espacio. */
export function permisosDe(store, workspaceId, nombre) {
  const workspace = store.getters['workspace/get'](workspaceId)
  return (workspace?._?.permissions || []).find((p) => p.name === nombre)
    ?.permissions
}

// --- Permisos por columna (gemelas de extras_mg/roles/campos.py) -------------

export const NADIE = 'NADIE'

// De más abierto a más cerrado, en el orden en que se ofrecen.
export const NIVELES = [EDITOR, ENCARGADO, ADMIN, NADIE]

export const NOMBRES_DE_NIVEL = {
  [EDITOR]: 'Quien edita la tabla',
  [ENCARGADO]: 'Miembros de la base o tabla, y admins',
  [ADMIN]: 'Solo admins',
  [NADIE]: 'Nadie (bloqueada)',
}

export const DESCRIPCIONES_DE_NIVEL = {
  [EDITOR]: 'Como cualquier otra columna: la escribe quien puede editar la tabla.',
  [ENCARGADO]:
    'Solo los Miembros de esta tabla o de su base, y los admins del espacio.',
  [ADMIN]: 'Solo los admins del espacio.',
  [NADIE]: 'Nadie la escribe ni la cambia hasta que alguien la desbloquee.',
}

const NIVELES_DEL_ENCARGADO = [EDITOR, ENCARGADO, NADIE]

/**
 * Los niveles que esa persona puede ponerle a la columna; vacío si no la
 * configura. El admin, todos; el Encargado de la tabla o de su base, los suyos
 * (no "solo admins"), y no toca una columna que el admin dejó así.
 * `permisos` es el objeto de 'mg_roles'.
 */
export function nivelesQuePuedePoner(permisos, aplicacionId, tablaId, nivelActual) {
  if (!permisos) return []
  if (permisos.rol_espacio === 'ADMIN') return [...NIVELES]
  if (!puedeAdministrar(permisos, aplicacionId, tablaId)) return []
  if (nivelActual === ADMIN) return []
  return [...NIVELES_DEL_ENCARGADO]
}

const ESCRIBIR_COLUMNA = [
  'database.table.field.write_values',
  'database.table.field.update',
  'database.table.field.delete',
]
const LLENAR_EN_FORMULARIO = 'database.table.field.submit_anonymous_values'

/**
 * Si esa operación sobre la columna se niega. `permisos` es el objeto de
 * 'mg_permisos_de_campo': el servidor ya calculó con campos.niega_campo qué
 * columnas no escribe esta persona y cuáles no van en formularios.
 */
export function niegaCampo(permisos, operacion, campoId) {
  if (!permisos || campoId === undefined || campoId === null) return false
  if (ESCRIBIR_COLUMNA.includes(operacion)) {
    return (permisos.no_escribe || []).includes(campoId)
  }
  if (operacion === LLENAR_EN_FORMULARIO) {
    return (permisos.fuera_de_formularios || []).includes(campoId)
  }
  return false
}
